// @ts-nocheck
import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { DATE_FORMAT, NOOP } from '../../const/CommonConst';

const handleMouseDown = (e) => e.preventDefault();

const DatePicker = ({
  error,
  helperText,
  disableBackspace = false,
  required = false,
  onBlur = NOOP,
  height = '44px', // Added height prop with default value
  maxDate = null,
  minDate = null,
  open,
  onOpen,
  onClose,
  ...props
}) => {
  const handleKeyDown = (event) => {
    if (disableBackspace && event?.key === 'Backspace') {
      event.preventDefault();
    }
  };

  const inputRef = useRef(null);

  // This logic is implemented for onBlur event as onBlur is not supported by Mui by default
  useEffect(() => {
    const handleBlur = (event) => {
      if (onBlur && !inputRef?.current?.contains(event?.relatedTarget)) {
        onBlur(event);
      }
    };

    const inputNode = inputRef.current;
    if (inputNode) {
      inputNode.addEventListener('blur', handleBlur, true);
    }

    return () => {
      if (inputNode) {
        inputNode.removeEventListener('blur', handleBlur, true);
      }
    };
  }, [onBlur]);

  // v7 requires moment objects — coerce strings/timestamps to moment
  const safeValue = useMemo(() => {
    if (props.value == null) return null;
    if (moment.isMoment(props.value)) return props.value;
    const m = moment(props.value);
    return m.isValid() ? m : null;
  }, [props.value]);

  const [isOpenState, setIsOpenState] = useState(false);
  const isOpen = open !== undefined ? open : isOpenState;

  const handleOpenInternal = useCallback(() => {
    setIsOpenState(true);
    if (onOpen) onOpen();
  }, [onOpen, setIsOpenState]);

  const handleCloseInternal = useCallback(() => {
    setIsOpenState(false);
    if (onClose) onClose();
  }, [onClose, setIsOpenState]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MuiDatePicker
        {...props}
        value={safeValue}
        open={isOpen}
        onOpen={handleOpenInternal}
        onClose={handleCloseInternal}
        maxDate={maxDate}
        minDate={minDate}
        slotProps={{
          ...props?.slotProps,
          // eslint-disable-next-line react/prop-types
          textField: {
            error: !!error,
            helperText,
            required,
            onKeyDown: handleKeyDown,
            inputRef,
            onClick: handleOpenInternal,
            onMouseDown: handleMouseDown,
            sx: {
              '& .MuiInputBase-root': {
                height, // Set the height dynamically for the input base
                cursor: 'pointer',
              },
              '& .MuiInputBase-input': {
                cursor: 'pointer',
              },
              ...props?.slotProps?.textField?.sx,
            },
            ...props?.slotProps?.textField,
          },
        }}
        format={DATE_FORMAT}
      />
    </LocalizationProvider>
  );
};

DatePicker.propTypes = {
  error: PropTypes.bool.isRequired,
  helperText: PropTypes.string.isRequired,
  disableBackspace: PropTypes.bool,
  required: PropTypes.bool.isRequired,
  onBlur: PropTypes.func,
  height: PropTypes.string, // Added height prop type
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
  ]),
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  maxDate: PropTypes.instanceOf(Object),
  minDate: PropTypes.instanceOf(Object),
  entityDetailsFormikForm: PropTypes.shape({}),
  attribute: PropTypes.shape({}),
  slotProps: PropTypes.shape({
    textField: PropTypes.shape({
      sx: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
        PropTypes.func,
      ]),
    }),
  }),
};

export default DatePicker;
